// NEW SALES ORDER PAGE - shared display bits. DISPLAY ONLY: no money arithmetic here (the view model carries exact text).
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoneyExact, formatDate, formatQty } from '../../../utils/format';
import { IcoLn } from '../../../shell/ActionIcons';
import { Emo } from '../../../shell/EmojiIcons';
import { TextTooltip } from '../../../components/HelpIcon';
import TableToolbar from '../../../components/TableToolbar';

export const OrderPageCtx = createContext(null);
export const useOP = () => useContext(OrderPageCtx);

export const M = (v) => formatMoneyExact(v);
export const N = (v) => (v === '0.00' ? formatMoneyExact(v) : `-${formatMoneyExact(v)}`);   // a deduction printed with its minus (no "-0")
export const D = (v) => (v ? formatDate(v) : '—');
export const Q = (v) => formatQty(v);

// HAVE / DERIVED / NEW tag (toggle in the page bar, default off). `field` is looked up in the model's temp list: a derived-in-the-adapter figure shows "temp".
export function Tag({ kind = 'have', field }) {
  const o = useOP(); const { t } = useTranslation('orderPage');
  if (!o || !o.tagsOn) return null;
  const temp = field && o.vm && o.vm.temp && o.vm.temp.includes(field);
  const k = temp ? 'temp' : kind;
  return <sup className={`np-tag np-tag-${k}`} title={field || undefined} data-testid="np-tag">{t('tag.' + k)}</sup>;
}

export function Chip({ tone = 'mut', children, title, testid, ...rest }) {
  return <span className={`chip ${tone}`} title={title} data-testid={testid} {...rest}>{children}</span>;
}

// a badge object { key, tone, n, amount } printed with the orderPage catalog: badge.<group>.<key>
export function BadgeChip({ b, group }) {
  const { t } = useTranslation('orderPage');
  const text = t(`badge.${group}.${b.key}`, { n: b.n, amount: b.amount != null ? M(b.amount) : '' });
  const tip = t(`badge.${group}.${b.key}_tip`, { n: b.n, amount: b.amount != null ? M(b.amount) : '', defaultValue: '' });
  return <Chip tone={b.tone} title={tip || undefined} testid={`np-badge-${group}-${b.key}`}>{text}</Chip>;
}

export function reasonText(t, state) {
  if (!state || state.enabled || !state.code) return '';
  const p = state.params || {};
  const fm = Object.fromEntries(Object.entries(p).map(([k, v]) => [k, /^-?\d+(\.\d+)?$/.test(String(v)) ? M(v) : v]));
  return t('reason.' + state.code, fm);
}

// ONE label per action; disabled = visible + greyed + the REASON on hover (title on the button itself, exactly like the current order page) + aria-label "<label> - <reason>"
export function Act({ k, state, primary = false, danger = false, label, icon, testid, size = 'sm', onClick }) {
  const o = useOP(); const { t } = useTranslation('orderPage');
  const st = state || (o && o.vm.actions[k]) || { enabled: false, code: null };
  const text = label || t('act.' + k);
  const why = reasonText(t, st);
  const off = !st.enabled;
  const cls = `btn ${size}${primary ? ' primary' : ''}${danger ? ' danger' : ''}`;
  return (
    <button type="button" className={cls} disabled={off} aria-disabled={off} title={off ? why || undefined : undefined} aria-label={off && why ? `${text} - ${why}` : undefined}
      data-testid={testid || `np-act-${k}`} onClick={() => { if (!off) (onClick || (() => o.run(k)))(); }}>
      {icon ? <IcoLn name={icon} /> : null}{icon ? ' ' : ''}{text}
    </button>
  );
}

// a tie chip: green "Ties" or amber with the exact difference on hover
export function TieChip({ tie, label }) {
  const { t } = useTranslation('orderPage');
  if (!tie) return null;
  return tie.ties
    ? <span className="chip good np-tie" title={t('tie.okTip')} data-testid="np-tie-ok">{t('tie.ok')}</span>
    : <span className="chip warn np-tie" title={t('tie.badTip', { delta: M(tie.delta), lhs: M(tie.lhs), rhs: M(tie.rhs), what: label || '' })} data-testid="np-tie-bad">{t('tie.bad', { delta: M(tie.delta) })}</span>;
}

export function Info({ text, label }) { return text ? <TextTooltip text={text} ariaLabel={label || text} /> : null; }

export function Pending({ what }) {
  const { t } = useTranslation('orderPage');
  return <div className="np-pending" data-testid="np-pending"><Emo name="lock" /> <b>{what}</b> <span>{t('pending.note')}</span></div>;
}

// 47109 R5f: a tab holding several tables shows ONE table at a time behind a sub-tab bar INSIDE the tab (the Invoices pattern: odtabs + subtab-count).
// The chosen sub-tab is in the URL (?sub=, replaceState like ?tab=) so a back navigation (A7m) reopens it; OrderPageSwitch drops it when the tab changes.
const readSub = () => { try { return new URLSearchParams(window.location.search).get('sub'); } catch { return null; } };
export function useSubTab(keys, fallback) {
  const [want, setWant] = useState(readSub);
  const sub = keys.includes(want) ? want : keys.includes(fallback) ? fallback : keys[0];
  const setSub = useCallback((k) => {
    try { const q = new URLSearchParams(window.location.search); q.set('sub', k); window.history.replaceState(window.history.state, '', `${window.location.pathname}?${q}${window.location.hash}`); } catch { /* no history (tests) */ }
    setWant(k);
  }, []);
  return [sub, setSub];
}
// id = the tab ('deliv' -> data-testid np-deliv-subtabs / np-delivtab-<key>); counts[k] = the rows of that table (null = no count chip)
export function SubTabs({ id, keys, sub, setSub, counts = {} }) {
  const { t } = useTranslation('orderPage');
  if (keys.length < 2) return null;   // 47122 (F3c): one table needs no tab strip
  return (
    <div className="odx"><div className="odtabs" role="tablist" data-testid={`np-${id}-subtabs`}>
      {keys.map(k => <button key={k} type="button" role="tab" aria-selected={sub === k} className={`odtab${sub === k ? ' on' : ''}`} data-testid={`np-${id}tab-${k}`} onClick={() => setSub(k)}>
        {t(`${id}.sub.${k}`)}{counts[k] != null ? <span className="subtab-count">{counts[k]}</span> : null}</button>)}
    </div></div>
  );
}

// zone title = the shared table toolbar (sits on the page background above a table), exactly like the current tabs
export function ZoneHead({ title, meta, right, tag }) {
  return <TableToolbar title={<>{title}{tag}</>} meta={meta}>{right}</TableToolbar>;
}

// the story sentence of an Activity row (also the timeline marker hover): translation key story.<type> + text params (money / dates formatted here, never computed)
export function storyText(t, row) {
  const p = row.params || {};
  const fm = Object.fromEntries(Object.entries(p).map(([k, v]) => [k, /^-?\d+\.\d\d$/.test(String(v)) ? M(v) : /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? D(v) : v]));
  return t('story.' + row.type + (row.type === 'delivery' && p.value ? 'Value' : ''), fm);
}

// the '?' text of a refund due (see refundHelpOf): held money / credit, payable now with the reason, then the choices with their grey reasons
export function refundHelpText(t, h) {
  if (!h) return '';
  const L = [t('help.held', { money: M(h.money), credit: M(h.credit) }), t('help.payable', { amount: M(h.payable) })];
  h.reasons.forEach(r => L.push(t('help.why.' + r, { owed: M(h.owedElsewhere), credit: M(h.credit) })));
  const pay = h.pay.enabled ? t('help.ok') : reasonText(t, h.pay); const rel = h.release.enabled ? t('help.ok') : reasonText(t, h.release);
  L.push(t('help.pay', { s: pay }), t('help.release', { s: rel }), t('help.payout', { s: h.payout.enabled ? t('help.ok') : reasonText(t, h.payout) }), t('help.leave'));
  return L.join(' ');
}
export function RefundHelp({ vm }) {
  const { t } = useTranslation('orderPage'); const text = refundHelpText(t, vm.refundHelp);
  return text ? <span data-testid="np-refund-help"><TextTooltip text={text} ariaLabel={t('help.label')} /></span> : null;
}
